import { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useMatch } from "react-router-dom"
import classnames from "classnames"
import { useDialogs, useLoadingIndicator } from "modules/overlays"
import { useSite } from "modules/sites"
import { useAuth, useUser } from "modules/users"
import { useToggle } from "utils/state"

import Icon from "components/Icon"

import styles from "./EntryMenu.module.scss"

const Entry = ({ entry, onClick, onFocus }) => {
  const { t } = useTranslation("sales")

  const hanldleClick = useCallback(
    (event) => {
      if (entry.entries) {
        event.preventDefault()
      }
      if (onClick) {
        onClick(entry)
      }
    },
    [entry, onClick],
  )

  return entry ? (
    <NavLink className={styles.entry} onClick={hanldleClick} onFocus={onFocus} to={entry.path} end={entry.exact}>
      {entry.icon ? <Icon className={styles.icon} icon={entry.icon} /> : null}
      <span>{t(entry.title)}</span>
    </NavLink>
  ) : null
}

const EntrySection = ({ menu, expanded, expandMenu }) => {
  const containerRef = useRef()
  const [open, toggle] = useToggle(false)
  const active = useMatch({ path: `${menu.path}/*` })
  const { t } = useTranslation("sales")

  const openMenu = useCallback(() => {
    toggle(true)
    expandMenu()
  }, [toggle, expandMenu])

  useEffect(() => {
    if (containerRef.current?.querySelector('[aria-current="page"]')) {
      openMenu()
    }
  }, [openMenu])

  useEffect(() => {
    if (open) {
      expandMenu()
    }
  }, [open, expandMenu])

  return menu && menu.entries ? (
    <section
      ref={containerRef}
      className={classnames(styles.section, { [styles.active]: active })}
      role="treeitem"
      aria-selected={open}
      aria-expanded={open ? expanded && !!open : null}>
      <header onClick={expanded ? toggle : openMenu}>
        <Icon className={styles.icon} icon={menu.icon || "folder"} />
        <h2>{t(menu.title)}</h2>
        <Icon className={classnames(styles.icon, styles.indicator)} icon={open ? "minus" : "plus"} />
      </header>
      {menu.entries.map((entry) => (
        <Entry key={entry.path} entry={entry} onFocus={openMenu} />
      ))}
    </section>
  ) : null
}

const EntryMenu = ({ className, openNotificationPanel }) => {
  const [open, toggle] = useToggle(!window.matchMedia("(max-width: 1080px)").matches)
  const { t } = useTranslation(["authentication", "label"])
  const { logOut } = useAuth()
  const user = useUser()
  const { entries, indicatorColor } = useSite()
  const withLoadingIndicator = useLoadingIndicator()
  const { openErrorModal } = useDialogs()

  const expandMenu = useCallback(() => toggle(true), [toggle])

  const compactMenu = useCallback(() => toggle(false), [toggle])

  const userLogOut = useCallback(() => {
    withLoadingIndicator(
      logOut()
        .then(() => window.location.assign("/"))
        .catch((error) => openErrorModal({ message: error.message, title: t("authentication:error_logout") })),
    )
  }, [withLoadingIndicator, logOut, openErrorModal, t])

  useEffect(() => {
    window.matchMedia("(max-width: 1080px)").addEventListener("change", (event) => toggle(!event.target.matches))
  }, [toggle])

  const handleClick = (event) => {
    if (
      getComputedStyle(event.currentTarget)["position"] === "fixed" &&
      open &&
      ![event.target.nodeName, event.target.parentNode.nodeName].includes("HEADER")
    ) {
      compactMenu()
    }
  }

  return (
    <footer
      className={classnames(styles.container, className)}
      style={indicatorColor ? { "--indicator-color": indicatorColor } : null}
      aria-expanded={open ? !!open : null}
      onClick={handleClick}>
      <img className={styles.logo} src={`${process.env.PUBLIC_URL}/images/logo.svg`} alt="Gogoro" />
      <button className={styles.toggle} onClick={toggle}>
        <Icon className={styles.icon} icon={open ? "x" : "menu"} />
      </button>
      <nav className={styles.navigation}>
        {user?.notifications?.length > 0 && (
          <button className={styles.notification} type="button" onClick={openNotificationPanel}>
            <Icon className={styles.icon} icon="bell" />
            <span>{t("label:notification")}</span>
          </button>
        )}
        {entries?.map((entry) =>
          entry.entries ? (
            <EntrySection key={entry.path} menu={entry} expanded={open} expandMenu={expandMenu} />
          ) : (
            <Entry key={entry.path} entry={entry} />
          ),
        )}
      </nav>
      <hr />
      <button className={styles.logout} type="button" onClick={userLogOut}>
        <Icon className={styles.icon} icon="log-out" />
        <span>{t("label:logout")}</span>
      </button>
    </footer>
  )
}

export default EntryMenu
